// stores/websocketStore.js
import { defineStore } from 'pinia'
import { ref } from 'vue'
import eventBus from '@/plugins/eventBus'

export const useWebsocketStore = defineStore('websocketStore', () => {
  const socket = ref(null)
  const isConnected = ref(false)
  const reconnectAttempts = ref(0)
  const queue = []

  let reconnectTimer = null
  let connectionLossTimer = null
  let closedManually = false

  function clearConnectionLossTimer() {
    clearTimeout(connectionLossTimer)
    connectionLossTimer = null
  }

  function startConnectionLossTimer() {
    clearConnectionLossTimer()
    connectionLossTimer = setTimeout(() => {
      // Reconnection continues in the background, but the receiver must not
      // leave a stale game visible when its Worker has been unavailable.
      console.warn('[Receiver] Worker unavailable for 30 seconds; resetting display')
      eventBus.emit('worker-connection-timeout')
    }, 30000)
  }

  async function connect({ uid, name, width, height, env }) {
    const host = window.location.hostname
    const workerBase = process.env.NODE_ENV === 'development'
      ? `http://${host}:8787`
      : 'https://worker.smeegoon.com'
    const websocketBase = workerBase.replace(/^http/, 'ws')
    const params = new URLSearchParams({ uid, name, width: String(width), height: String(height) })
    const url = `${websocketBase}/receiver?${params}`

    closedManually = false
    eventBus.emit('receiver-debug', { message: `Worker WebSocket: ${url}` })
    let capability
    try {
      capability = await getReceiverCapability(workerBase, { uid, name })
    } catch (error) {
      console.error('[Receiver] Could not authenticate receiver', error)
      eventBus.emit('receiver-debug', { message: 'Receiver authentication failed' })
      if (error?.code === 'receiver-already-claimed') {
        eventBus.emit('receiver-identity-conflict', { name })
        return
      }
      startConnectionLossTimer()
      attemptReconnect({ uid, name, width, height, env })
      return
    }

    const ws = new WebSocket(url, ['smeegoon-receiver', `auth.${capability}`])
    socket.value = ws

    ws.onopen = () => {
      console.log('[Receiver] WebSocket connected')
      eventBus.emit('receiver-debug', { message: `Worker connected. Room code: ${name}` })
      isConnected.value = true
      reconnectAttempts.value = 0
      clearConnectionLossTimer()
      flushQueue()
      eventBus.emit('name', { env, name })
    }

        ws.onmessage = (msg) => {
          const raw = JSON.parse(msg.data)
          console.log('[Receiver] Message received:', raw)

          // The ReceiverWebsocketDO wraps messages as: { type, data: { event, content, sender } }
          if (raw.data && raw.data.event) {
            const event = raw.data.event
            const content = raw.data.content ?? {}
        
            console.log(`[Receiver] Bus Emit: ${event}`, content)
            // Emit the clean event name (e.g., 'sync', 'init', 'launch') to match App.vue
            eventBus.emit(event, content)
          }
        }

    ws.onerror = (err) => {
      console.error('[Receiver] WebSocket error', err)
       eventBus.emit('receiver-debug', { message: 'Worker WebSocket error' })
       eventBus.emit('error', { env, err })
    }

    ws.onclose = () => {
      console.warn('[Receiver] Disconnected')
      eventBus.emit('receiver-debug', { message: 'Worker disconnected; reconnecting' })
      isConnected.value = false
      socket.value = null
      if (!closedManually) {
        startConnectionLossTimer()
        attemptReconnect({ uid, name, width, height, env })
      }
    }
  }

  function flushQueue() {
    if (socket.value?.readyState === WebSocket.OPEN) {
      while (queue.length > 0) {
        socket.value.send(queue.shift())
      }
    }
  }

  function send(payload) {
    const message = typeof payload === 'string' ? payload : JSON.stringify(payload)
    if (socket.value?.readyState === WebSocket.OPEN) {
      socket.value.send(message)
    } else {
      queue.push(message)
    }
  }

  function disconnect(code = 1000, reason = 'Client closed') {
    closedManually = true
    clearTimeout(reconnectTimer)
    clearConnectionLossTimer()
    if (socket.value) socket.value.close(code, reason)
    socket.value = null
    isConnected.value = false
  }

  function attemptReconnect({ uid, name, width, height, env }) {
    const delay = Math.min(1000 * 2 ** reconnectAttempts.value, 30000)
    reconnectAttempts.value++
    console.log(`[Receiver] Reconnecting in ${delay / 1000}s`)

    reconnectTimer = setTimeout(() => {
      connect({ uid, name, width, height, env })
    }, delay)
  }

  async function getReceiverCapability(workerBase, { uid, name }) {
    const storageKey = `smeegoon-receiver-capability:${name}`
    const existing = localStorage.getItem(storageKey) || ''
    const response = await fetch(`${workerBase}/receiver-capability`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ uid, name, capability: existing }),
    })
    const body = await response.json().catch(() => ({}))
    if (!response.ok || !body.capability) {
      const error = new Error(body.error || `receiver-auth-${response.status}`)
      error.code = body.error
      throw error
    }
    localStorage.setItem(storageKey, body.capability)
    return body.capability
  }

  return {
    socket,
    isConnected,
    connect,
    send,
    disconnect,
  }
})
